import { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { AiFillSetting } from "react-icons/ai";
import { BsCalendar3, BsDownload, BsGrid3X3, BsSearch, BsX } from "react-icons/bs";
import { FaRoad } from "react-icons/fa";
import { DeveloperDataOptions, ShowDeveloperData } from "../../../config/developerData";
import MapLoadIndicator from "./MapLoadIndicator";

const externalStyles = [
  { id: "codd-leaflet-css", href: "/map_runtime/vendor/leaflet.css" },
  { id: "codd-maplibre-css", href: "/map_runtime/vendor/maplibre-gl.css" },
  { id: "codd-map-runtime-css", href: "/map_runtime/styles.css" },
];

const vendorScripts = [
  { id: "codd-leaflet-js", src: "/map_runtime/vendor/leaflet.js" },
  { id: "codd-turf-js", src: "/map_runtime/vendor/turf.min.js" },
  { id: "codd-h3-js", src: "/map_runtime/vendor/h3-js.umd.js" },
  { id: "codd-xlsx-js", src: "/map_runtime/vendor/xlsx.full.min.js" },
  { id: "codd-factor-labels-ru", src: "/map_runtime/factor_labels_ru.js" },
];
const runtimeScript = { id: "codd-map-runtime-app", src: "/map_runtime/app_leaflet.js" };
const offlineMapScripts = [
  { id: "codd-maplibre-js", src: "/map_runtime/vendor/maplibre-gl.js" },
  { id: "codd-leaflet-maplibre-js", src: "/map_runtime/vendor/leaflet-maplibre-gl.js" },
];

function ensureStylesheet({ id, href }) {
  const existing = document.getElementById(id);
  if (existing) return;

  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = href;
  document.head.appendChild(link);
}

function loadScript({ id, src }) {
  return new Promise((resolve, reject) => {
    const existing = document.getElementById(id);

    if (existing?.dataset.loaded === "true") {
      resolve();
      return;
    }

    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error(`Failed to load ${src}`)), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.id = id;
    script.src = src;
    script.async = true;
    script.onload = () => {
      script.dataset.loaded = "true";
      resolve();
    };
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.body.appendChild(script);
  });
}

function removeScript(id) {
  const existing = document.getElementById(id);
  if (existing?.parentNode) {
    existing.parentNode.removeChild(existing);
  }
}

function CrossingIcon({ iconKey }) {
  return (
    <img
      className="layer-switch-icon-image"
      src={`/map_runtime/icons/crossings/${iconKey}.svg`}
      alt=""
      aria-hidden="true"
    />
  );
}

function LineSwitchIcon({ kind }) {
  return <span className={`layer-line-swatch is-${kind}`} />;
}

function LayerSwitch({ id, children, defaultChecked = false, nested = false, icon = null, iconClassName = "" }) {
  return (
    <label className={`layer-switch${nested ? " is-nested" : ""}`}>
      {icon ? (
        <span className={`layer-switch-leading-icon ${iconClassName}`.trim()} aria-hidden="true">
          {icon}
        </span>
      ) : null}
      <span className="layer-switch-text">{children}</span>
      <input type="checkbox" id={id} defaultChecked={defaultChecked} />
      <span className="toggle-switch" aria-hidden="true" />
    </label>
  );
}

function SettingsContent() {
  return (
    <div className="codd-settings-sheet">
      <div className="panel-scroll layer-checklist">
        <section className="settings-group">
          <div className="settings-group-title">Гексагоны</div>
          <div className="settings-group-body">
            <LayerSwitch id="hexLayerToggle">Гексагоны</LayerSwitch>
            <LayerSwitch id="hexColorToggle" nested>Окрас гексов</LayerSwitch>
            <div id="hexColorBox" className="panel-box hidden">
              <details className="hex-color-settings">
                <summary>Настроить окраску</summary>
                <div className="hex-color-settings-content">
                  <div className="panel-row">
                    <span className="panel-label">Метод окраски</span>
                    <select id="hexColorMode" className="panel-select" defaultValue="thresholds">
                      <option value="thresholds">Пороги ДТП</option>
                      <option value="quantile">Квантили</option>
                      <option value="similarity">Относительно среднего</option>
                      <option value="factor_outcome">Ожидание и факт</option>
                    </select>
                  </div>
                  <div id="hexColorLegend" className="color-legend" />
                </div>
              </details>
            </div>

            <LayerSwitch id="factorAnalysisToggle">Факторный анализ ДТП</LayerSwitch>
            <div id="factorAnalysisBox" className="panel-box hidden">
              <div id="factorAnalysisStatus" className="panel-hint">Факторный анализ выключен</div>
              <LayerSwitch id="factorAnalysisForecastDtpOnlyToggle" nested>Только прогноз ДТП</LayerSwitch>
              <LayerSwitch id="factorAnalysisBadHexesOnlyToggle" nested>Показать «плохие» гексы</LayerSwitch>
            </div>
          </div>
        </section>

        <section className="settings-group">
          <div className="settings-group-title">Дороги</div>
          <div className="settings-group-body">
            <LayerSwitch id="roadsLayerToggle">Дороги</LayerSwitch>
            <LayerSwitch id="roadLaneLabelsToggle" nested>Число полос на дорогах</LayerSwitch>
            <LayerSwitch id="constructionRoadsToggle" nested>Строящиеся дороги</LayerSwitch>
            <LayerSwitch id="proposedRoadsToggle" nested>Проектируемые дороги</LayerSwitch>
            <LayerSwitch id="roadsColorToggle" nested>Аварийность (окрас дорог)</LayerSwitch>
            <div id="roadsColorBox" className="panel-box hidden">
              <div className="panel-row">
                <span className="panel-label">Метод окраски дорог</span>
                <select id="roadsColorMode" className="panel-select" defaultValue="thresholds">
                  <option value="thresholds">Пороги ДТП</option>
                  <option value="quantile">Квантили</option>
                  <option value="similarity">Относительно среднего</option>
                  <option value="severity">По тяжести</option>
                </select>
              </div>
              <div id="roadsColorLegend" className="color-legend" />
            </div>
          </div>
        </section>

        <section className="settings-group">
          <div className="settings-group-title">ДТП</div>
          <div className="settings-group-body">
            <LayerSwitch id="accidentsLayerToggle">ДТП (точки)</LayerSwitch>
            <div id="accidentsBox" className="panel-box hidden">
              <div className="panel-row">
                <span className="panel-label">Типы ДТП</span>
              </div>
              <div className="panel-actions">
                <button id="accidentTypesSelectAllBtn" className="btn small" type="button">Выбрать все</button>
                <button id="accidentTypesClearBtn" className="btn secondary small" type="button">Снять выбор</button>
              </div>
              <div id="accidentTypes" className="panel-scroll panel-checklist" />
              <div id="accidentTypesStatus" className="panel-hint">Типы ДТП не загружены</div>
              <div className="panel-source-grid">
                <LayerSwitch id="accidentsInjuredToggle" defaultChecked nested>С пострадавшими</LayerSwitch>
                <LayerSwitch id="accidentsNoInjuriesToggle" defaultChecked nested>Без пострадавших</LayerSwitch>
              </div>
              <LayerSwitch id="accidentRedGreenMarkersToggle" defaultChecked nested>Красный/зеленый маркер ДТП</LayerSwitch>
              <LayerSwitch id="accidentsNeighborHexToggle" defaultChecked nested>ДТП соседних районов в крайних гексах</LayerSwitch>
              <div className="panel-date-grid">
                <div className="panel-row">
                  <span className="panel-label">Дата с</span>
                  <input id="accidentsDateFrom" className="panel-select" type="date" />
                </div>
                <div className="panel-row">
                  <span className="panel-label">Дата по</span>
                  <input id="accidentsDateTo" className="panel-select" type="date" />
                </div>
              </div>
              <div className="panel-actions">
                <button id="accidentsDateResetBtn" className="btn secondary small" type="button">Сброс дат</button>
              </div>
              <div id="accidentsYearButtons" className="panel-year-grid">
                <button className="btn secondary small" type="button" data-acc-year="2020">2020</button>
                <button className="btn secondary small" type="button" data-acc-year="2021">2021</button>
                <button className="btn secondary small" type="button" data-acc-year="2022">2022</button>
                <button className="btn secondary small" type="button" data-acc-year="2023">2023</button>
                <button className="btn secondary small" type="button" data-acc-year="2024">2024</button>
                <button className="btn secondary small" type="button" data-acc-year="2025">2025</button>
              </div>
              <div id="accidentsCount" className="panel-hint">Показано ДТП: 0</div>
            </div>
            <LayerSwitch id="clustersLayerToggle">Кластеры ДТП</LayerSwitch>
            <div id="clustersBox" className="panel-box hidden">
              <div className="panel-date-grid">
                <div className="panel-row">
                  <span className="panel-label">Радиус, м</span>
                  <input id="clusterEpsMeters" className="panel-select" type="number" min="1" max="2000" step="1" defaultValue="10" />
                </div>
                <div className="panel-row">
                  <span className="panel-label">Мин. размер</span>
                  <input id="clusterMinSize" className="panel-select" type="number" min="2" max="1000" step="1" defaultValue="3" />
                </div>
              </div>
              <div className="panel-actions">
                <button id="clustersRefreshBtn" className="btn small" type="button">Обновить</button>
                <button id="clustersClearBtn" className="btn secondary small" type="button">Сброс</button>
              </div>
              <div id="clustersStatus" className="panel-hint">Выберите гексы</div>
            </div>
          </div>
        </section>

        <section className="settings-group">
          <div className="settings-group-title">Объекты</div>
          <div className="settings-group-body">
            <LayerSwitch id="intersectionsLayerToggle">Перекрестки</LayerSwitch>

            <LayerSwitch id="poiLayerToggle">POI (категории)</LayerSwitch>
            <div id="poiCategoryBox" className="panel-box hidden">
              <div className="panel-actions">
                <button id="poiSelectAllBtn" className="btn small" type="button">Выбрать все</button>
                <button id="poiClearBtn" className="btn secondary small" type="button">Снять выбор</button>
              </div>
              <div id="poiCategories" className="panel-scroll" />
            </div>

            <LayerSwitch
              id="crossingsLayerToggle"
              icon={<CrossingIcon iconKey="pedestrian" />}
            >
              Переходы
            </LayerSwitch>
            <div id="crossingsTypeBox" className="panel-box hidden">
              <div className="panel-actions">
                <button id="crossingsSelectAllBtn" className="btn small" type="button">Выбрать все</button>
                <button id="crossingsClearBtn" className="btn secondary small" type="button">Снять выбор</button>
              </div>
              <div id="crossingsTypes" className="panel-scroll" />
            </div>

            <LayerSwitch
              id="signalsLayerToggle"
              icon={<CrossingIcon iconKey="traffic_signal" />}
            >
              Светофоры
            </LayerSwitch>
            <LayerSwitch id="railCrossingsLayerToggle">
              Пересечения ЖД и трамвайных путей
            </LayerSwitch>
            <div id="railCrossingsTypeBox" className="panel-box hidden">
              <LayerSwitch id="railCrossingsRailwayToggle" defaultChecked nested>
                Через ЖД
              </LayerSwitch>
              <LayerSwitch id="railCrossingsTramToggle" defaultChecked nested>
                Через трамвайные пути
              </LayerSwitch>
              <div id="railCrossingsStatus" className="panel-hint">Выберите район или гексагоны</div>
            </div>
            <LayerSwitch
              id="railLinesLayerToggle"
              icon={<LineSwitchIcon kind="rail" />}
            >
              ЖД линии
            </LayerSwitch>

            <LayerSwitch
              id="tramLinesLayerToggle"
              icon={<LineSwitchIcon kind="tram" />}
            >
              Трамвайные линии
            </LayerSwitch>
          </div>
        </section>
      </div>

      <section className="settings-group">
        <div className="settings-group-title">Инструменты</div>
        <div className="settings-group-body">
          <LayerSwitch id="measureToggle">Линейка</LayerSwitch>
          <div id="measureSummary" className="panel-hint">Длина: 0 м</div>
          <button id="measureClearBtn" className="btn secondary small" type="button">Сбросить</button>
        </div>
      </section>

      <div className="panel-row">
        <span className="panel-label">Базовый слой карты</span>
        <select id="baseLayerSelect" className="panel-select" />
        <div id="offlineBaseLayerStatus" className="panel-hint">Проверяем локальную карту…</div>
      </div>

      <div className="panel-row">
        <span className="panel-label">Приоритет клика</span>
        <select id="layerPrioritySelect" className="panel-select" defaultValue="auto">
          <option value="auto">Авто</option>
          <option value="districts">Районы</option>
          <option value="hexes">Гексагоны</option>
          <option value="roads">Дороги</option>
          <option value="accidents">ДТП</option>
          <option value="clusters">Кластеры ДТП</option>
          <option value="intersections">Перекрестки</option>
          <option value="rail_crossings">Пересечения ЖД и трамвайных путей</option>
          <option value="poi">POI</option>
          <option value="crossings">Переходы</option>
          <option value="signals">Светофоры</option>
          <option value="rail_lines">ЖД линии</option>
          <option value="tram_lines">Трамвайные линии</option>
        </select>
      </div>

      <div className="panel-hint">Кликните по гексагону, чтобы загрузить дороги.</div>
      <div id="selectedHexCount" className="panel-hint">Выбрано гексов: 0</div>
      {DeveloperDataOptions.mapTechnicalData ? (
        <>
          <button id="downloadRoadsBtn" className="btn hidden" disabled type="button">Скачать дороги</button>
          <button id="downloadIntersectionsBtn" className="btn secondary hidden" disabled type="button">Скачать перекрестки</button>
          <button id="downloadIntersectionsXlsxBtn" className="btn secondary hidden" disabled type="button">Скачать перекрестки (XLSX)</button>
        </>
      ) : null}
    </div>
  );
}

function SearchContent() {
  return (
    <div className="codd-settings-sheet">
      <div className="panel-box">
        <div className="panel-title">Поиск по карте</div>
        <div className="panel-source-grid">
          <label><input type="radio" name="mapSearchMode" id="mapSearchModeAuto" value="auto" defaultChecked /> Автоматически (WIP)</label>
          <label><input type="radio" name="mapSearchMode" id="mapSearchModeCoords" value="coords" /> Координаты</label>
          <label><input type="radio" name="mapSearchMode" id="mapSearchModeHex" value="hex" /> Hex</label>
          <label><input type="radio" name="mapSearchMode" id="mapSearchModeAccident" value="accident" /> ID ДТП</label>
        </div>
        <div className="panel-row">
          <span className="panel-label">Запрос</span>
          <input id="mapSearchInput" className="panel-select" type="text" placeholder="55.75, 37.62 / 8a... / id" />
        </div>
        <div className="panel-actions">
          <button id="mapSearchBtn" className="btn small" type="button">Найти</button>
          <button id="mapSearchClearBtn" className="btn secondary small" type="button">Сброс</button>
        </div>
        <div id="mapSearchStatus" className="panel-hint" />
      </div>
    </div>
  );
}

function DistrictContent() {
  return (
    <div className="codd-settings-sheet district-sheet">
      <label><input type="checkbox" id="districtsLayerToggle" defaultChecked /> Районы</label>
      <div id="districtsBox" className="panel-box hidden district-panel">
        <div className="district-panel-head">
          <input id="districtSearch" className="panel-select district-search" type="text" placeholder="Поиск района или АО..." />
          <div id="districtSelectionMeta" className="panel-hint district-selection-meta">Выберите АО целиком или раскройте список районов.</div>
        </div>
        <div id="districtTree" className="panel-scroll district-tree" />
        <div className="district-panel-actions">
          <div className="panel-actions">
            <button id="districtApplyBtn" className="btn small" type="button">Загрузить гексы</button>
            <button id="districtClearBtn" className="btn secondary small" type="button">Сброс</button>
          </div>
          <button id="districtFitBtn" className="btn secondary small" type="button">Приблизить район</button>
          <div className="panel-hint district-panel-hint">Можно выбрать весь АО одним кликом или отметить отдельные районы внутри него.</div>
        </div>
      </div>
    </div>
  );
}

function DateContent() {
  return (
    <div className="codd-settings-sheet">
      <div id="dateDrawerPanel" className="panel-box codd-date-panel" />
    </div>
  );
}

function RoadDateContent() {
  return (
    <div className="codd-settings-sheet">
      <div className="panel-box">
        <div className="panel-title">Дата дорог</div>
        <div className="panel-row">
          <span className="panel-label">Год</span>
          <select id="roadsYearSelect" className="panel-select" defaultValue="2025">
            <option value="2025">2025</option>
            <option value="2024">2024</option>
            <option value="2023">2023</option>
            <option value="2022">2022</option>
            <option value="2021">2021</option>
            <option value="2020">2020</option>
            <option value="2019">2019</option>
          </select>
        </div>
        <div className="panel-hint">Выбор меняет только дорожный слой на основной карте.</div>
      </div>
    </div>
  );
}

export default function MapWorkspace() {
  const [loadError, setLoadError] = useState("");
  const [showSettings, setShowSettings] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showDistricts, setShowDistricts] = useState(false);
  const [showDates, setShowDates] = useState(false);
  const [showRoadDates, setShowRoadDates] = useState(false);
  const [mapLoadingState, setMapLoadingState] = useState(null);

  useEffect(() => {
    const handleMapLoading = (event) => setMapLoadingState(event.detail || null);
    window.addEventListener("codd-map-runtime-loading", handleMapLoading);
    const initialState = window.getMapApiLoadingState?.();
    if (initialState) setMapLoadingState(initialState);
    return () => window.removeEventListener("codd-map-runtime-loading", handleMapLoading);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const configuredApiBaseUrl =
      window.CODD_MAP_CONFIG?.apiBaseUrl ||
      window.WEB_OSM_MAP_CONFIG?.apiBaseUrl ||
      process.env.REACT_APP_CODD_API_URL ||
      "";
    const isLocalReactDevelopment =
      ["localhost", "127.0.0.1"].includes(window.location.hostname) &&
      window.location.port === "3000";

    const runtimeConfig = {
      ...(window.WEB_OSM_MAP_CONFIG || {}),
      ...(window.CODD_MAP_CONFIG || {}),
      apiBaseUrl: isLocalReactDevelopment && (!configuredApiBaseUrl || configuredApiBaseUrl === "/api")
        ? "http://127.0.0.1:8000"
        : (configuredApiBaseUrl || "http://127.0.0.1:8000"),
      // The map is API-only. Static JSON/GeoJSON is not a runtime fallback.
      allowMapDataFallback: false,
      showDeveloperData: ShowDeveloperData,
      dataRoot: window.CODD_MAP_CONFIG?.dataRoot || window.WEB_OSM_MAP_CONFIG?.dataRoot || "/map_data",
      roadYears:
        window.CODD_MAP_CONFIG?.roadYears ||
        window.WEB_OSM_MAP_CONFIG?.roadYears ||
        [2025, 2024, 2023, 2022, 2021, 2020, 2019],
      twoGisApiKey:
        window.CODD_MAP_CONFIG?.twoGisApiKey ||
        window.WEB_OSM_MAP_CONFIG?.twoGisApiKey ||
        "8c4753d6-e6e7-42e5-8b91-6a16e0e78c40",
      clusterApiUrl:
        window.CODD_MAP_CONFIG?.clusterApiUrl ||
        window.WEB_OSM_MAP_CONFIG?.clusterApiUrl ||
        "http://127.0.0.1:8010",
    };

    window.CODD_MAP_CONFIG = runtimeConfig;
    window.WEB_OSM_MAP_CONFIG = runtimeConfig;

    externalStyles.forEach(ensureStylesheet);

    (async () => {
      try {
        await Promise.all(vendorScripts.map(loadScript));
        if (cancelled) return;
        try {
          for (const script of offlineMapScripts) await loadScript(script);
        } catch (error) {
          console.warn("Offline map renderer is unavailable", error);
        }
        if (cancelled) return;
        await loadScript(runtimeScript);
      } catch (error) {
        if (!cancelled) {
          setLoadError(error instanceof Error ? error.message : "Не удалось загрузить карту");
        }
      }
    })();

    return () => {
      cancelled = true;
      removeScript("codd-map-runtime-app");
    };
  }, []);

  const closeLeftDrawers = () => {
    setShowSettings(false);
    setShowSearch(false);
  };

  return (
    <div className="codd-map-workspace">
      <div id="map" className="codd-map-canvas" />

      <div className="codd-top-left-rail">
        <div className="codd-settings-trigger">
          <Button
            variant="dark"
            onClick={() => {
              setShowSearch(false);
              setShowSettings(true);
            }}
            className="buttons_custom_value"
          >
            <AiFillSetting className="icons_custom" /> Настройки
          </Button>
        </div>

        <div className="codd-search-trigger">
          <Button
            variant="dark"
            onClick={() => {
              setShowSettings(false);
              setShowSearch(true);
            }}
            className="buttons_custom_value"
          >
            <BsSearch className="icons_custom" /> Поиск
          </Button>
          <MapLoadIndicator state={mapLoadingState} />
        </div>
        {DeveloperDataOptions.factorLoadDetails ? (
          <div id="factorDataSourceStatus" className="factor-data-source-status is-loading" role="status" aria-live="polite">
            Факторные данные: проверка БД…
          </div>
        ) : null}
      </div>

      <div className={`codd-drawer-backdrop ${showSettings || showSearch ? "is-open" : ""}`} onClick={closeLeftDrawers} />
      <aside className={`codd-drawer codd-drawer-left ${showSettings ? "is-open" : ""}`}>
        <div className="codd-drawer-header">
          <div className="codd-drawer-title">Настройки карты</div>
          <button type="button" className="codd-drawer-close" aria-label="Закрыть" onClick={() => setShowSettings(false)}>
            <BsX aria-hidden="true" />
          </button>
        </div>
        <div className="codd-drawer-body">
          <SettingsContent />
        </div>
      </aside>

      <aside className={`codd-drawer codd-drawer-left ${showSearch ? "is-open" : ""}`}>
        <div className="codd-drawer-header">
          <div className="codd-drawer-title">Поиск</div>
          <button type="button" className="codd-drawer-close" aria-label="Закрыть" onClick={() => setShowSearch(false)}>
            <BsX aria-hidden="true" />
          </button>
        </div>
        <div className="codd-drawer-body">
          <SearchContent />
        </div>
      </aside>

      <div className="codd-top-right-rail">
        <div className="codd-period-summary">
          <div id="mapDateSummary" className="codd-date-summary">Период: не выбран</div>
          <div id="mapRoadYearSummary" className="codd-road-year-summary">Год дорог: 2025</div>
        </div>

        <div className="codd-road-date-trigger">
          <Button
            variant="dark"
            onClick={() => {
              setShowDates(false);
              setShowDistricts(false);
              setShowRoadDates(true);
            }}
            className="buttons_custom_value"
          >
            <FaRoad className="icons_custom" /> <span id="roadDateButtonLabel">Дороги</span>
          </Button>
        </div>

        <div className="codd-date-trigger">
          <Button
            variant="dark"
            onClick={() => {
              setShowRoadDates(false);
              setShowDistricts(false);
              setShowDates(true);
            }}
            className="buttons_custom_value"
          >
            <BsCalendar3 className="icons_custom" /> Дата
          </Button>
        </div>

        <div className="codd-district-trigger">
          <Button
            variant="dark"
            onClick={() => {
              setShowDates(false);
              setShowRoadDates(false);
              setShowDistricts(true);
            }}
            className="buttons_custom_value"
          >
            <BsGrid3X3 className="icons_custom" /> Районы
          </Button>
        </div>
      </div>

      <div className={`codd-drawer-backdrop ${showDates ? "is-open" : ""}`} onClick={() => setShowDates(false)} />
      <aside className={`codd-drawer codd-drawer-right ${showDates ? "is-open" : ""}`}>
        <div className="codd-drawer-header">
          <div className="codd-drawer-title">Дата</div>
          <button type="button" className="codd-drawer-close" aria-label="Закрыть" onClick={() => setShowDates(false)}>
            <BsX aria-hidden="true" />
          </button>
        </div>
        <div className="codd-drawer-body">
          <DateContent />
        </div>
      </aside>

      <div className={`codd-drawer-backdrop ${showRoadDates ? "is-open" : ""}`} onClick={() => setShowRoadDates(false)} />
      <aside className={`codd-drawer codd-drawer-right ${showRoadDates ? "is-open" : ""}`}>
        <div className="codd-drawer-header">
          <div className="codd-drawer-title">Дата дорог</div>
          <button type="button" className="codd-drawer-close" aria-label="Закрыть" onClick={() => setShowRoadDates(false)}>
            <BsX aria-hidden="true" />
          </button>
        </div>
        <div className="codd-drawer-body">
          <RoadDateContent />
        </div>
      </aside>

      <div className={`codd-drawer-backdrop ${showDistricts ? "is-open" : ""}`} onClick={() => setShowDistricts(false)} />
      <aside className={`codd-drawer codd-drawer-right ${showDistricts ? "is-open" : ""}`}>
        <div className="codd-drawer-header">
          <div className="codd-drawer-title">Районы</div>
          <button type="button" className="codd-drawer-close" aria-label="Закрыть" onClick={() => setShowDistricts(false)}>
            <BsX aria-hidden="true" />
          </button>
        </div>
        <div className="codd-drawer-body">
          <DistrictContent />
        </div>
      </aside>

      <div
        id="factorAnalysisSelectionPanel"
        className="factor-selection-drawer hidden is-collapsed"
        data-show-developer-data={DeveloperDataOptions.factorTechnicalData ? "true" : "false"}
      />

      <div className="codd-bottom-stack">
        <div className="codd-legend-slot">
          <div id="mapLegend" className="map-legend collapsed">
            <button id="mapLegendToggle" className="map-legend-toggle" type="button" aria-expanded="false">Показать легенду</button>
            <div id="mapLegendBody" className="map-legend-body hidden">
              <div className="map-legend-section">
                <div className="map-legend-header">
                  <div className="map-legend-title">Гексагоны</div>
                  <div className="map-legend-header-actions">
                    {DeveloperDataOptions.mapTechnicalData ? (
                      <button
                        id="downloadSelectedHexesBtn"
                        className="map-legend-icon-btn"
                        type="button"
                        aria-label="Выгрузить выбранные гексы"
                        title="Выгрузить выбранные гексы"
                      >
                        <BsDownload aria-hidden="true" />
                      </button>
                    ) : null}
                    <button id="hexLegendInfoBtn" className="map-legend-info-btn" type="button">Как работает</button>
                  </div>
                </div>
                <div id="legendHexStats" className="map-legend-content" />
              </div>
              <div className="map-legend-section">
                <div className="map-legend-header">
                  <div className="map-legend-title">Типы дорог</div>
                  <button id="roadTypeLegendInfoBtn" className="map-legend-info-btn" type="button">Как работает</button>
                </div>
                <div id="legendRoadTypeStats" className="map-legend-content" />
              </div>
              <div className="map-legend-section">
                <div className="map-legend-header">
                  <div className="map-legend-title">Дороги</div>
                  <button id="roadLegendInfoBtn" className="map-legend-info-btn" type="button">Как работает</button>
                </div>
                <div id="legendRoadLaneStats" className="map-legend-content" />
              </div>
                <div className="map-legend-section">
                  <div className="map-legend-header">
                    <div className="map-legend-title">Окрас дорог</div>
                    <button id="roadColorLegendInfoBtn" className="map-legend-info-btn" type="button">Как работает</button>
                  </div>
                  <div id="legendRoadColorStats" className="map-legend-content" />
                </div>
                <div className="map-legend-section">
                  <div className="map-legend-header">
                    <div className="map-legend-title">Переходы и линии</div>
                  </div>
                  <div id="legendCrossingsStats" className="map-legend-content" />
                </div>
              </div>
            </div>
          </div>

      </div>

      <div id="legendHelpModal" className="legend-help-modal hidden" aria-hidden="true">
        <div className="legend-help-backdrop" />
        <div className="legend-help-dialog" role="dialog" aria-modal="true" aria-labelledby="legendHelpTitle">
          <div className="legend-help-topbar">
            <div id="legendHelpTitle" className="legend-help-title">Как работают режимы окраски</div>
            <button id="legendHelpCloseBtn" className="legend-help-close" type="button" aria-label="Закрыть">
              <BsX aria-hidden="true" />
            </button>
          </div>
          <div id="legendHelpBody" className="legend-help-grid" />
        </div>
      </div>

      {loadError ? <div className="codd-map-error">{loadError}</div> : null}
    </div>
  );
}
